import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getVehiclesInventory,
  addInventory,
  updateInventory,
  deleteInventory,
} from "../../../../../../apis/Business/InventoryApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  getTransportCountryData,
  getRegisteredVehicleTypesDropdown,
} from "../../../../../../apis/Dropdown";

const InventoryVehicleTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownData, setDropdownData] = useState([]);
  const [dropdownLocation, setDropdownLocation] = useState([]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getVehiclesInventory({ businessId: businessId, setObjects: setData });
    getTransportCountryData({
      setDropdownData: setDropdownLocation,
    });
    getRegisteredVehicleTypesDropdown({
      businessId: businessId,
      setDropdownData: setDropdownData,
    });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      updateInventory({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        subcategory: "locality_vehicles",
        type_vehicle: editedItem["type_vehicle.value"]
          ? editedItem["type_vehicle.value"].value
          : editedItem["type_vehicle"]
          ? editedItem["type_vehicle"].value
          : null,
        type_chemical: null,
        type_buildingcode: null,
        type_packaging: null,
        type_service: null,
        type_machinery: null,
        type_buildingmaterial: null,
        location: editedItem["location.value"]
          ? editedItem["location.value"].value
          : editedItem["location"]
          ? editedItem["location"].value
          : null,
        subcategoryId: editedItem["subcategory_obj"],
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addInventory({
        businessId,
        ...newItem,
        type_vehicle: newItem["type_vehicle.value"]
          ? newItem["type_vehicle.value"].value
          : null,
        subcategory: "locality_vehicles",
        type_chemical: null,
        type_buildingcode: null,
        type_packaging: null,
        type_service: null,
        type_machinery: null,
        type_buildingmaterial: null,
        location: newItem["location.value"]
          ? newItem["location.value"].value
          : null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteInventory({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "invoice_date",
        header: t("INVOICE_DATE"),
        type: "text",
        canHide: true,
        canSort: true,
        placeholder: t("DD_MM_YYYY"),
        optional: true,
        width: "80px",
      },
      {
        accessor: "invoice_number",
        header: t("INVOICE_NO"),
        type: "text",
        canSort: true,
        canHide: true,
        optional: true,
        width: "80px",
      },
      {
        accessor: "nickname",
        header: t("INV_NICKNAME"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "80px",
      },
      {
        accessor: "location.value",
        header: t("PRODUCTION_COUNTRY"),
        type: "dropdown",
        dropdownData: dropdownLocation,
        disableSortBy: true,
        width: "110px",
        optional: true,
      },
      {
        accessor: "type_vehicle.value",
        header: t("TYPE_VEHICLE"),
        type: "dropdown",
        dropdownData: dropdownData,
        disableSortBy: true,
        width: "110px",
        tipHeader: "Tip",
        tipBody: t("TIP_TYPE_VEHICLE"),
        isCategory: true,
        optional: true,
      },
      {
        accessor: "qty",
        header: t("INV_QTY_VEHICLE"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
      },
      {
        accessor: "life_expectancy",
        header: t("LIFE_EXPECTANCY"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
        unit: t("YEAR"),
      },
      {
        accessor: "manuel",
        header: t("INV_VEH_MANUEL"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("TIP_TYPE_MANUEL"),
      },
      {
        accessor: "spacer2",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "price_invoice",
        header: t("PRICE_INVOICE"),
        type: "number",
        canSort: true,
        canHide: true,
        placeholder: "0",
        optional: true,
        subtotalUnit: "DKK",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("PRICE_INVOICE_TIP"),
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "80px",
      },
    ],
    [dropdownData, dropdownLocation, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    price_invoice: { label: t("TOTAL_PRICE") },
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "type_vehicle.value": dropdownData,
      "location.value": dropdownLocation,
    }),
    [dropdownData, dropdownLocation]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
    />
  );
};

export default InventoryVehicleTable;
